/* ==========================================================================
   Oaken Vault — product page behaviour

   Two jobs:
   1. Dispatch line — picks the correct "when will this be packed" message
      against a Danish business calendar.
   2. Free shipping line — toggles between the two states on variant change.

   Why client-side: Shopify caches product sections, so a server-rendered
   time is stale. And this theme has no Section Rendering API — variant
   changes are DOM-only — so anything price-dependent must be toggled here.
   ========================================================================== */

(function () {
  'use strict';

  // The script tag is emitted from more than one place on purpose: the trust
  // strip can be fully suppressed (non-DK + TCG), so relying on it alone would
  // silently kill the stock colouring. Duplicate <script src> tags execute the
  // IIFE more than once, so guard rather than trying to guarantee exactly one.
  if (window.ovPdpLoaded) return;
  window.ovPdpLoaded = true;

  /* ---------------------------------------------------------------- config */

  var CONFIG = {
    cutoffHour: 15,          // pack anything ordered before this hour
    cutoffMinute: 0,
    closedWeekdays: [0, 6],  // 0 = Sunday, 6 = Saturday

    // Recurring non-holiday closures, "MM-DD". Not official public holidays,
    // but you are almost certainly not packing. Delete any you do work.
    extraClosedDates: ['06-05', '12-24', '12-31'],

    // One-off closures: conventions, holiday, moving week. "YYYY-MM-DD".
    // Add here and every product page updates itself.
    // e.g. ['2026-10-12','2026-10-13']
    closures: [],

    // TCG is stocked deep and bought in multiples, so its stock tiers are the
    // theme's own thresholds multiplied. Scaling rather than hardcoding means
    // changing the thresholds in the theme editor still propagates here.
    // Theme is 1 / 2, so TCG becomes 3 / 6: red at 1-3, orange 4-6, green 7+.
    tcgMultiplier: 3
  };

  var STRINGS = {
    da: {
      today:    'Afsendes i dag',
      todayRest: function (c) { return ', bestil inden ' + c; },
      next:     function (w) { return 'Afsendes ' + w; },
      fallback: 'Afsendes samme hverdag',
      tomorrow: 'i morgen',
      onDay:    function (d) { return 'på ' + d; },
      onDate:   function (d, m) { return 'd. ' + d + '. ' + m; },
      weekdays: ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'],
      months:   ['januar', 'februar', 'marts', 'april', 'maj', 'juni',
                 'juli', 'august', 'september', 'oktober', 'november', 'december'],
      clock:    function (h, m) { return m === 0 ? 'kl. ' + h : 'kl. ' + h + '.' + pad(m); }
    },
    en: {
      today:    'Shipped today',
      todayRest: function (c) { return ', order before ' + c; },
      next:     function (w) { return 'Shipped ' + w; },
      fallback: 'Shipped same business day',
      tomorrow: 'tomorrow',
      onDay:    function (d) { return d; },
      onDate:   function (d, m) { return d + ' ' + m; },
      weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      months:   ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'],
      clock:    function (h, m) { return h + ':' + pad(m); }
    }
  };

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  /* ------------------------------------------------------- civil date utils */
  // Everything works on a plain {y,m,d} "civil date" and uses UTC only as
  // day-arithmetic scaffolding. No timezone maths, so no DST bugs.

  // Constructing an Intl.DateTimeFormat is comparatively expensive, and this
  // runs on load, on variant change and on every wake — so build it once.
  var cphFormat = null;

  function nowInCopenhagen() {
    if (!cphFormat) {
      cphFormat = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Copenhagen',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      });
    }
    var parts = cphFormat.formatToParts(new Date());

    var get = function (type) {
      var part = parts.find(function (p) { return p.type === type; });
      return part ? parseInt(part.value, 10) : 0;
    };

    return { y: get('year'), m: get('month'), d: get('day'), H: get('hour'), M: get('minute') };
  }

  function weekdayOf(date) {
    return new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay();
  }

  function addDays(date, n) {
    var stepped = new Date(Date.UTC(date.y, date.m - 1, date.d + n));
    return { y: stepped.getUTCFullYear(), m: stepped.getUTCMonth() + 1, d: stepped.getUTCDate() };
  }

  function mmdd(date) { return pad(date.m) + '-' + pad(date.d); }
  function isoOf(date) { return date.y + '-' + mmdd(date); }

  /* ----------------------------------------------------- Danish public days */
  // Computed, not hardcoded, so the list never goes stale.
  // Store Bededag is deliberately absent — abolished as a public holiday
  // in Denmark from 2024.

  function easterSunday(year) {
    var a = year % 19,
        b = Math.floor(year / 100),
        c = year % 100,
        d = Math.floor(b / 4),
        e = b % 4,
        f = Math.floor((b + 8) / 25),
        g = Math.floor((b - f + 1) / 3),
        h = (19 * a + b - d - g + 15) % 30,
        i = Math.floor(c / 4),
        k = c % 4,
        l = (32 + 2 * e + 2 * i - h - k) % 7,
        m = Math.floor((a + 11 * h + 22 * l) / 451),
        month = Math.floor((h + l - 7 * m + 114) / 31),
        day = ((h + l - 7 * m + 114) % 31) + 1;
    return { y: year, m: month, d: day };
  }

  var holidayCache = {};

  function publicHolidays(year) {
    if (holidayCache[year]) return holidayCache[year];

    var easter = easterSunday(year);
    holidayCache[year] = [
      { y: year, m: 1, d: 1 },    // Nytårsdag
      addDays(easter, -3),        // Skærtorsdag
      addDays(easter, -2),        // Langfredag
      easter,                     // Påskedag
      addDays(easter, 1),         // 2. påskedag
      addDays(easter, 39),        // Kristi himmelfartsdag
      addDays(easter, 49),        // Pinsedag
      addDays(easter, 50),        // 2. pinsedag
      { y: year, m: 12, d: 25 },  // Juledag
      { y: year, m: 12, d: 26 }   // 2. juledag
    ].map(isoOf);

    return holidayCache[year];
  }

  function isBusinessDay(date) {
    if (CONFIG.closedWeekdays.indexOf(weekdayOf(date)) !== -1) return false;
    if (CONFIG.extraClosedDates.indexOf(mmdd(date)) !== -1) return false;
    if (CONFIG.closures.indexOf(isoOf(date)) !== -1) return false;
    if (publicHolidays(date.y).indexOf(isoOf(date)) !== -1) return false;
    return true;
  }

  function nextBusinessDay(from) {
    for (var i = 1; i <= 30; i++) {
      var candidate = addDays(from, i);
      if (isBusinessDay(candidate)) return { date: candidate, offset: i };
    }
    return null; // 30 closed days in a row means the config is wrong
  }

  /* -------------------------------------------------------------- messaging */

  function buildMessage(lang) {
    var S = STRINGS[lang] || STRINGS.da;
    var now = nowInCopenhagen();
    var today = { y: now.y, m: now.m, d: now.d };
    var minutesNow = now.H * 60 + now.M;
    var minutesCutoff = CONFIG.cutoffHour * 60 + CONFIG.cutoffMinute;
    var clock = S.clock(CONFIG.cutoffHour, CONFIG.cutoffMinute);

    if (isBusinessDay(today) && minutesNow < minutesCutoff) {
      return { strong: S.today, rest: S.todayRest(clock), state: 'today' };
    }

    var next = nextBusinessDay(today);
    if (!next) {
      // Fail safe: say the vague-but-true thing rather than nothing.
      return { strong: S.fallback, rest: S.todayRest(clock), state: 'fallback' };
    }

    var when;
    if (next.offset === 1) {
      when = S.tomorrow;
    } else if (next.offset <= 6) {
      when = S.onDay(S.weekdays[weekdayOf(next.date)]);
    } else {
      when = S.onDate(next.date.d, S.months[next.date.m - 1]);
    }

    return { strong: S.next(when), rest: '', state: 'next' };
  }

  /* ----------------------------------------------------------------- render */

  function langOf(node) {
    var scope = node.closest('[data-ov-lang]');
    return scope && scope.getAttribute('data-ov-lang') === 'en' ? 'en' : 'da';
  }

  function renderDispatch() {
    document.querySelectorAll('[data-ov-dispatch]').forEach(function (node) {
      var strong = node.querySelector('[data-ov-dispatch-strong]');
      if (!strong) return;

      var message = buildMessage(langOf(node));
      var rest = node.querySelector('[data-ov-dispatch-rest]');

      strong.textContent = message.strong;
      if (rest) rest.textContent = message.rest;
      node.setAttribute('data-ov-dispatch-state', message.state);
    });
  }

  function renderFreeShipping(priceInCents) {
    document.querySelectorAll('[data-ov-fs]').forEach(function (node) {
      var threshold = parseInt(node.getAttribute('data-ov-fs-threshold'), 10);
      if (isNaN(threshold) || typeof priceInCents !== 'number') return;

      var over = node.querySelector('[data-ov-fs-over]');
      var under = node.querySelector('[data-ov-fs-under]');
      if (!over || !under) return;

      var qualifies = priceInCents >= threshold;
      over.classList.toggle('ov-hide', !qualifies);
      under.classList.toggle('ov-hide', qualifies);
    });
  }

  /* ---------------------------------------------------------------- stock */
  // The theme renders low stock and healthy stock with identical markup —
  // no modifier class — so CSS can't distinguish them on its own. Read the
  // quantity against the theme's own low-stock threshold and add the hook.
  // Deliberately does not touch the text: that lives in a locale string.

  // "TCG > MTG > The Hobbit > Play Booster" -> "TCG".
  // Read from Shopify's analytics meta so no theme file has to expose it.
  // If that object is ever absent this returns '' and TCG simply falls back
  // to the theme's own thresholds — degraded, never broken.
  function productTypeRoot() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    var type = (meta && meta.product && meta.product.type) || '';
    return type.split('>')[0].trim();
  }

  // Pre-order pages emit a marker from Liquid — the theme exposes no template
  // name to JS, and body classes don't carry it either.
  function isPreorder() {
    var el = document.querySelector('[data-ov-template]');
    var t = el ? el.getAttribute('data-ov-template') : '';
    return t.indexOf('preorder') !== -1 || t.indexOf('pre-order') !== -1;
  }

  function isEnglish() {
    return (document.documentElement.lang || 'da').toLowerCase().indexOf('en') === 0;
  }

  // On a pre-order the number isn't stock on a shelf, it's how many slots are
  // left to reserve. "Kun 1 tilbage på lager" next to a September delivery
  // date reads as a contradiction; this says what the number actually means.
  var PREORDER_STOCK = {
    da: { some: 'Kun [quantity] tilbage til pre-order', plenty: 'Tilgængelig til pre-order' },
    en: { some: 'Only [quantity] left for pre-order',   plenty: 'Available for pre-order' }
  };

  function levelFor(qty, low, veryLow) {
    if (isNaN(qty) || qty <= 0) return null;
    if (!isNaN(veryLow) && qty <= veryLow) return 'very_low';
    if (!isNaN(low) && qty <= low) return 'low';
    return 'normal';
  }

  // Writing to the notice would retrigger our own observer, so pause it.
  function writeNotice(el, notice, text) {
    if (el.ovObserver) el.ovObserver.disconnect();
    notice.textContent = text;
    if (el.ovObserver) {
      el.ovObserver.observe(el, { childList: true, subtree: true, characterData: true });
    }
  }

  function stockState(el) {
    var notice = el.querySelector('.js-inventory-notice');
    if (!notice) return;

    var qty = currentStock(el).qty;
    var low = parseInt(el.dataset.thresholdLow, 10);
    var veryLow = parseInt(el.dataset.thresholdVeryLow, 10);

    var isTcg = productTypeRoot() === 'TCG';
    if (isTcg) {
      low = low * CONFIG.tcgMultiplier;
      veryLow = veryLow * CONFIG.tcgMultiplier;
    }

    var level = levelFor(qty, low, veryLow);

    // Two cases need the wording rewritten:
    //  - pre-order, where "på lager" is simply the wrong noun
    //  - TCG, where the theme computes wording from the UNSCALED thresholds and
    //    would disagree with our colour: orange text reading "På lager"
    if (level) {
      var wanted = '';

      if (isPreorder()) {
        var po = PREORDER_STOCK[isEnglish() ? 'en' : 'da'];
        wanted = level === 'normal' ? po.plenty : po.some.replace('[quantity]', qty);
      } else if (isTcg) {
        var strings = (window.theme && window.theme.strings) || {};
        wanted = level === 'normal'
          ? (strings.inStock || '')
          : (strings.onlyXLeft || '').replace('[quantity]', qty);
      }

      if (wanted && notice.textContent.trim() !== wanted) {
        writeNotice(el, notice, wanted);
      }
    }

    // The theme leaves the notice EMPTY at healthy stock on non-TCG products.
    // Styling an empty span leaves a stray floating dot, so gate on real text.
    var hasText = notice.textContent.trim() !== '';
    el.classList.toggle('ov-stock--has-text', hasText);

    if (hasText && level) {
      el.setAttribute('data-ov-level', level);
    } else {
      el.removeAttribute('data-ov-level');
    }
  }

  // The theme does NOT refresh product-inventory's data attributes on variant
  // change: data-inventory-quantity, data-variant-available and
  // data-inventory-level keep whatever the server rendered. Verified on the
  // published theme as well, so this is the theme's own behaviour and not
  // something we introduced — but reading those attributes after a switch
  // means promising same-day dispatch on a variant that says "Udsolgt".
  //
  // The authoritative per-variant data is the JSON that the same element
  // carries; the selected id lives on the product form's hidden input, which
  // the theme does keep current (add-to-cart depends on it).
  function variantIndex(inv) {
    if (inv.ovVariants) return inv.ovVariants;
    inv.ovVariants = {};
    var script = inv.querySelector('script[type="application/json"]');
    if (script) {
      try {
        var rows = JSON.parse(script.textContent);
        for (var i = 0; i < rows.length; i++) inv.ovVariants[rows[i].id] = rows[i];
      } catch (e) { /* leave empty — callers fall back to the dataset */ }
    }
    return inv.ovVariants;
  }

  function selectedVariantId() {
    var input = document.querySelector('.js-product-form-main input[name="id"]')
             || document.querySelector('form[action*="/cart/add"] input[name="id"]')
             || document.querySelector('input[name="id"]');
    if (input && input.value) return input.value;
    var m = window.location.search.match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  // Falls back to the (possibly stale) dataset rather than returning nothing,
  // so a theme update that moves the JSON degrades to today's behaviour
  // instead of blanking the line.
  function currentStock(inv) {
    var id = selectedVariantId();
    var v = id ? variantIndex(inv)[id] : null;
    if (v) {
      return {
        available: v.available === true,
        qty: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : NaN,
        policy: v.inventory_policy
      };
    }
    return {
      available: inv.dataset.variantAvailable === 'true',
      qty: parseInt(inv.dataset.inventoryQuantity, 10),
      policy: inv.dataset.inventoryPolicy
    };
  }

  // On products where language is a variant option, the strip shows the
  // SELECTED language rather than every edition we stock, so it has to follow
  // the picker. Liquid hands over a raw-value -> display-value map so no
  // server round trip is needed (this theme has no Section Rendering API).
  function syncLanguage(variant) {
    var strip = document.querySelector('.ov-lang[data-ov-langopt]');
    if (!strip || !variant || !variant.options) return;

    var idx = parseInt(strip.getAttribute('data-ov-langopt'), 10);
    var raw = variant.options[idx];
    if (isNaN(idx) || raw == null) return;

    // Target the line span, not the container: the container also holds the
    // overflow <details> on multi-value boxes, and writing textContent to it
    // would delete that markup. A language variant always has exactly one
    // value, so the first line is the right one.
    var val = strip.querySelector('[data-ov-langval] .ov-lang__line')
           || strip.querySelector('[data-ov-langval]');
    if (!val) return;

    if (!strip.ovLangMap) {
      try { strip.ovLangMap = JSON.parse(strip.getAttribute('data-ov-langmap') || '{}'); }
      catch (e) { strip.ovLangMap = {}; }
    }

    // Unmapped values pass through untranslated rather than disappearing.
    val.textContent = strip.ovLangMap[raw] || raw;
  }

  /* ------------------------------------------------------------ fact boxes */
  // The wide boxes cap at two LINES, never two values. Those are not the same
  // thing: one value per line is the rule, but a long name wraps and costs two
  // lines on its own. Measured inside a three-up box, usable text width is
  // 110px and "Joshua Yearsley" is 110px at 14/800 — so Arcs rendered Cole
  // Wehrle, Joshua, Yearsley: three lines.
  //
  // Liquid cannot make this decision. The usable width depends on the width of
  // the product column and on how many boxes share the row, and the container
  // queries mean it changes as the window changes. So the split is made here,
  // after layout, and remade on resize.
  //
  // Nothing is truncated and nothing is dropped: values that do not fit move
  // into the same <details> the overflow already uses, and the +N count goes
  // up to match. Without JS every value up to the Liquid limit still renders —
  // a slightly taller box, which is today's behaviour, not a broken one.
  var FACT_LINE_CAP = 2;

  function factState(box) {
    if (box.ovFact) return box.ovFact;
    var val = box.querySelector('[data-ov-langval]');
    if (!val) return null;
    var spans = val.querySelectorAll('.ov-lang__line');
    var items = [];
    for (var i = 0; i < spans.length; i++) items.push(spans[i].textContent);
    var det = val.querySelector('.ov-lang__more');
    box.ovFact = { val: val, items: items, open: det ? det.open : false, drawn: -1 };
    return box.ovFact;
  }

  function factLine(text) {
    var s = document.createElement('span');
    s.className = 'ov-lang__line';
    s.textContent = text;
    return s;
  }

  // Built as nodes rather than innerHTML: these are names off a metafield and
  // an ampersand or a stray angle bracket should never be able to reach the
  // parser.
  function renderFact(box, visible) {
    var st = box.ovFact;
    var frag = document.createDocumentFragment();
    var i;

    for (i = 0; i < visible; i++) frag.appendChild(factLine(st.items[i]));

    var hidden = st.items.length - visible;
    if (hidden > 0) {
      var det = document.createElement('details');
      det.className = 'ov-lang__more';
      det.open = st.open;

      var sum = document.createElement('summary');
      var inLbl = document.createElement('span');
      inLbl.className = 'ov-lang__more-in';
      inLbl.textContent = '+' + hidden;
      var outLbl = document.createElement('span');
      outLbl.className = 'ov-lang__more-out';
      outLbl.textContent = '−' + hidden;
      sum.appendChild(inLbl);
      sum.appendChild(outLbl);
      det.appendChild(sum);

      for (i = visible; i < st.items.length; i++) det.appendChild(factLine(st.items[i]));
      frag.appendChild(det);
    }

    st.val.textContent = '';
    st.val.appendChild(frag);
    st.drawn = visible;
  }

  // Only the lines outside the <details> count toward the cap.
  function visibleLineHeight(val) {
    var total = 0;
    for (var i = 0; i < val.children.length; i++) {
      var child = val.children[i];
      if (child.classList.contains('ov-lang__line')) total += child.offsetHeight;
    }
    return total;
  }

  function capFactBox(box) {
    // The language box is skipped: it always holds exactly one value, and
    // syncLanguage rewrites that value on variant change. Re-rendering it from
    // the list captured at load would put the first variant's language back.
    if (box.hasAttribute('data-ov-langopt')) return;

    var st = factState(box);
    if (!st || st.items.length === 0) return;

    var live = st.val.querySelector('.ov-lang__more');
    if (live) st.open = live.open;

    var lh = parseFloat(getComputedStyle(st.val).lineHeight) || 17;
    var budget = lh * FACT_LINE_CAP + 1;

    var visible = Math.min(st.items.length, FACT_LINE_CAP);
    renderFact(box, visible);

    // Stops at one: a single name that still wraps past two lines keeps its
    // second line rather than vanishing behind a +1 that hides everything.
    while (visible > 1 && visibleLineHeight(st.val) > budget) {
      visible--;
      renderFact(box, visible);
    }
  }

  function capFactBoxes() {
    document.querySelectorAll('.ov-spec__wide .ov-lang').forEach(capFactBox);
  }

  // toggle does not bubble, so this listens in the capture phase. Remembering
  // the open state matters because a resize re-renders the box underneath an
  // open list.
  document.addEventListener('toggle', function (evt) {
    var det = evt.target;
    if (!det.classList || !det.classList.contains('ov-lang__more')) return;
    var box = det.closest('.ov-lang');
    if (box && box.ovFact) box.ovFact.open = det.open;
  }, true);

  var factTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(factTimer);
    factTimer = setTimeout(capFactBoxes, 120);
  });

  // Mirror of the Liquid guard, for variant switches.
  function syncDispatchVisibility() {
    var item = document.querySelector('[data-ov-dispatch]');
    var inv = document.querySelector('product-inventory');
    if (!item || !inv) return;

    var s = currentStock(inv);

    // qty 0 + deny + available means inventory isn't tracked — still shippable.
    var backordered = s.available && !isNaN(s.qty) && s.qty <= 0 && s.policy === 'continue';
    item.classList.toggle('ov-hide', !s.available || backordered);
  }

  function renderStock() {
    document.querySelectorAll('product-inventory').forEach(function (el) {
      stockState(el);

      // The notice is written asynchronously and rewritten on variant change,
      // so watch it rather than guessing at timings. Observing children only,
      // never attributes, so toggling our own class can't re-trigger this.
      if (!el.ovObserver) {
        el.ovObserver = new MutationObserver(function () { stockState(el); });
        el.ovObserver.observe(el, { childList: true, subtree: true, characterData: true });
      }
    });
  }

  /* ----------------------------------------------------------------- shine */

  // Two sweeps across the trust shield, once, when it first comes into view.
  // The animation lives in CSS behind .is-shining; all this does is decide
  // when. Deliberately one-shot: it does not re-arm when the shield scrolls
  // out and back, because someone scrolling up and down the page is not
  // arriving at the block for the first time and does not need telling twice.
  //
  // No IntersectionObserver (or no JS at all) means no shine. That is fine
  // here in a way it was not for the fact-box expander: this is decoration,
  // and nothing is unreachable without it. The reduced-motion check is a
  // belt-and-braces duplicate of the media query around the CSS.
  function armShieldShine() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.querySelectorAll('.product-details__block.panel').forEach(function (el) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.disconnect();
          el.classList.add('is-shining');
        });
      }, { threshold: 0.35 });
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------ wire */

  function init() {
    renderDispatch();
    renderStock();
    syncDispatchVisibility();
    capFactBoxes();
    armShieldShine();
    // Open Sans arrives after first paint, and it is wider than the fallback,
    // so a box measured before it lands can be measured one line short.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(capFactBoxes);
    // The theme populates the inventory notice asynchronously.
    setTimeout(function () { renderStock(); syncDispatchVisibility(); }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // This theme fires its own event on variant change (assets/variant-picker.js).
  // There is no Section Rendering API here, so this is the only hook.
  document.addEventListener('on:variant:change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    if (variant && typeof variant.price === 'number') renderFreeShipping(variant.price);
    syncLanguage(variant);
    setTimeout(function () { renderStock(); syncDispatchVisibility(); }, 50);
  });

  // "Sådan fungerer forudbestilling" opens the pre-order accordion, which now
  // sits below the buy button. Matched on the summary text rather than an id,
  // because theme-editor blocks don't expose a stable one.
  document.addEventListener('click', function (evt) {
    var link = evt.target.closest && evt.target.closest('[data-ov-open-preorder]');
    if (!link) return;
    evt.preventDefault();

    var panels = document.querySelectorAll('details');
    for (var i = 0; i < panels.length; i++) {
      var summary = panels[i].querySelector('summary');
      if (summary && /pre-?order|forudbestil/i.test(summary.textContent)) {
        panels[i].open = true;
        panels[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
  });

  document.addEventListener('shopify:section:load', renderDispatch);

  // Re-run if the tab has been left open across the cutoff.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) renderDispatch();
  });

  // Rather than polling every minute, wake once at the moment the message
  // can actually change: the cutoff if it's still ahead, otherwise midnight,
  // when a new day may reopen dispatch. One timer instead of 1,440 a day.
  function scheduleNextChange() {
    var now = nowInCopenhagen();
    var minutesNow = now.H * 60 + now.M;
    var minutesCutoff = CONFIG.cutoffHour * 60 + CONFIG.cutoffMinute;

    var minutesUntil = minutesNow < minutesCutoff
      ? minutesCutoff - minutesNow
      : (24 * 60) - minutesNow;

    // +5s so we land the other side of the boundary, never exactly on it.
    setTimeout(function () {
      renderDispatch();
      scheduleNextChange();
    }, (minutesUntil * 60 + 5) * 1000);
  }
  scheduleNextChange();
})();
